
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/contexts/auth";
import {
  downloadTemporaryFile,
  isFileReference,
  FileReference,
  cleanupTemporaryFiles,
  moveTemporaryToPermanent,
} from "@/utils/temporaryFileStorage";

interface UseDocumentSubmissionProps {
  formData: any;
  onSubmit: () => void;
  agreedToTerms: boolean;
}

export const useDocumentSubmission = ({
  formData,
  onSubmit,
  agreedToTerms,
}: UseDocumentSubmissionProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();

  // Helper function to get actual File object from either FileReference or direct File
  const getFileFromDocument = async (
    document: any
  ): Promise<{ file: File | null; fileReference: FileReference | null }> => {
    // Look for file in document_file property first, then file property
    const fileData = document.document_file || document.file;

    console.log("Processing document file:", fileData);

    // Check if it's a FileReference object
    if (isFileReference(fileData)) {
      console.log(
        "File is a FileReference, downloading from temporary storage..."
      );
      try {
        const downloadedFile = await downloadTemporaryFile(fileData);
        if (!downloadedFile) {
          console.error(
            "Failed to download file from temporary storage:",
            fileData.key
          );
          return { file: null, fileReference: null };
        }
        console.log("Successfully downloaded file:", downloadedFile.name);
        return { file: downloadedFile, fileReference: fileData };
      } catch (error) {
        console.error("Error downloading temporary file:", error);
        return { file: null, fileReference: null };
      }
    }

    // Check if it's a direct File object
    if (fileData instanceof File) {
      console.log("File is a direct File object:", fileData.name);
      return { file: fileData, fileReference: null };
    }

    // Check if it's a FileList
    if (fileData instanceof FileList && fileData.length > 0) {
      console.log("File is a FileList, using first file:", fileData[0].name);
      return { file: fileData[0], fileReference: null };
    }

    console.error("Invalid file format:", fileData);
    return { file: null, fileReference: null };
  };

  const submitDocuments = async (submissionFormData: any) => {
    if (!user) {
      toast.error("You must be logged in to submit documents");
      return false;
    }

    setIsSubmitting(true);

    try {
      console.log("Submitting documents with formData:", submissionFormData);

      const fileReferencesToCleanup: FileReference[] = [];
      let successfulSubmissions = 0;

      // Process each document
      for (const document of submissionFormData.documents) {
        const {
          document_type_id,
          document_number,
          issue_date,
          expiry_date,
          country_code,
          verification_purpose,
        } = document;

        console.log("Processing document:", document);

        // Get actual File object and track FileReference for cleanup
        const { file: actualFile, fileReference } = await getFileFromDocument(
          document
        );

        if (!actualFile) {
          toast.error(
            "Failed to process document file. Please try re-uploading the document."
          );
          continue;
        }

        // Track FileReference for cleanup if successful
        if (fileReference) {
          fileReferencesToCleanup.push(fileReference);
        }

        console.log(
          "Successfully obtained file:",
          actualFile.name,
          "Size:",
          actualFile.size,
          "Type:",
          actualFile.type
        );

        // Generate permanent file path
        const fileExt = actualFile.name.split(".").pop();
        const fileName = `${user.id}-${Date.now()}.${fileExt}`;
        const permanentPath = `verification/${fileName}`;

        let uploadSuccess = false;

        // Try to move from temporary storage first (if it's a FileReference)
        if (fileReference && fileReference.key.startsWith('temp/')) {
          console.log("Attempting to move file from temporary to permanent storage");
          uploadSuccess = await moveTemporaryToPermanent(fileReference.key, permanentPath);
          
          if (uploadSuccess) {
            console.log("File moved successfully using move operation");
          } else {
            console.log("Move operation failed, falling back to upload");
          }
        }

        // Fall back to direct upload if move failed or file is not from temp storage
        if (!uploadSuccess) {
          console.log("Uploading file to permanent storage:", permanentPath);

          const { error: uploadError } = await supabase.storage
            .from("identity_docs")
            .upload(permanentPath, actualFile);

          if (uploadError) {
            console.error("Upload error:", uploadError);
            toast.error(
              `Failed to upload ${actualFile.name}: ${uploadError.message}`
            );
            continue;
          }

          console.log("File uploaded successfully to:", permanentPath);
          uploadSuccess = true;
        }

        if (!uploadSuccess) {
          toast.error(`Failed to store ${actualFile.name}`);
          continue;
        }

        // Insert document record with constituency information
        const { error: insertError } = await supabase
          .from("user_id_documents")
          .insert({
            user_id: user.id,
            document_type_id,
            document_number: document_number || null,
            issue_date: issue_date || null,
            expiry_date: expiry_date || null,
            file_url: permanentPath,
            verification_status: "pending",
            verification_country: submissionFormData.verificationCountry,
            verification_purpose,
            constituency_code: submissionFormData.verificationConstituency,
            document_category: "identity",
            document_purpose: "verification",
          });

        if (insertError) {
          console.error("Insert error:", insertError);
          toast.error(
            `Failed to save document record for ${actualFile.name}: ${insertError.message}`
          );
          continue;
        }

        console.log("Document record saved successfully");
        successfulSubmissions++;
      }

      // Only proceed if at least one document was submitted successfully
      if (successfulSubmissions === 0) {
        toast.error("No documents were submitted successfully");
        return false;
      }

      // Update user profile verification status with additional personal info
      const profileUpdateData: any = {
        id_verification_status: "pending",
        constituency_code: submissionFormData.verificationConstituency,
        country_code: submissionFormData.verificationCountry,
      };

      // Add residency_status and date_of_birth from personalInfo if available
      if (submissionFormData.personalInfo) {
        if (submissionFormData.personalInfo.residency_status) {
          profileUpdateData.residency_status = submissionFormData.personalInfo.residency_status;
        }
        
        if (submissionFormData.personalInfo.date_of_birth) {
          // Ensure date is in proper format
          const dateOfBirth = submissionFormData.personalInfo.date_of_birth;
          if (dateOfBirth instanceof Date) {
            profileUpdateData.date_of_birth = dateOfBirth.toISOString().split('T')[0];
          } else if (typeof dateOfBirth === 'string') {
            profileUpdateData.date_of_birth = dateOfBirth;
          }
        }

        // Also update name fields if they exist
        if (submissionFormData.personalInfo.first_name) {
          profileUpdateData.first_name = submissionFormData.personalInfo.first_name;
        }
        if (submissionFormData.personalInfo.middle_name) {
          profileUpdateData.middle_name = submissionFormData.personalInfo.middle_name;
        }
        if (submissionFormData.personalInfo.last_name) {
          profileUpdateData.last_name = submissionFormData.personalInfo.last_name;
        }
      }

      const { error: profileError } = await supabase
        .from("profiles")
        .update(profileUpdateData)
        .eq("id", user.id);

      if (profileError) {
        console.error("Profile update error:", profileError);
        toast.error("Failed to update profile verification status");
        return false;
      }

      console.log("Profile updated successfully with personal info");

      // Clean up temporary files after successful submission
      if (fileReferencesToCleanup.length > 0) {
        try {
          await cleanupTemporaryFiles(fileReferencesToCleanup);
        } catch (cleanupError) {
          console.error("Error cleaning up temporary files:", cleanupError);
          // Don't fail the submission for cleanup errors
        }
      }

      toast.success(
        `${successfulSubmissions} document(s) submitted successfully for verification`
      );
      return true;
    } catch (error) {
      console.error("Document submission error:", error);
      toast.error("An unexpected error occurred while submitting documents");
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!agreedToTerms) {
      toast.error("You must agree to the terms and conditions");
      return;
    }

    const success = await submitDocuments(formData);
    if (success) {
      onSubmit();
    }
  };

  return {
    submitDocuments,
    isSubmitting,
    handleSubmit,
  };
};
